import React, { useMemo, useRef, useEffect, useState } from "react";
import { useProject } from "../state/projectStore";
import PageView from "./PageView";
import { DEFAULT_PX_PER_MM } from "../lib/units";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize } from "lucide-react";

export default function Canvas() {
  const { state, layout, page, setCurrentPage, setPreviewZoom } = useProject();
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-fit calculation: compute a base pxPerMm so the paper fits the visible area.
  const pxPerMm = useMemo(() => {
    if (!containerSize.w) return DEFAULT_PX_PER_MM * state.previewZoom;
    const availW = containerSize.w - 80; // ruler + padding
    const availH = containerSize.h - 120; // status bar + ruler + padding
    if (availW <= 0 || availH <= 0) return DEFAULT_PX_PER_MM * state.previewZoom;
    const fitX = availW / page.width_mm;
    const fitY = availH / page.height_mm;
    const fit = Math.min(fitX, fitY);
    return Math.max(0.3, fit * state.previewZoom);
  }, [page.width_mm, page.height_mm, state.previewZoom, containerSize.w, containerSize.h]);

  const fitToScreen = () => setPreviewZoom(1);
  const zoomIn = () => setPreviewZoom(Math.min(4, state.previewZoom * 1.25));
  const zoomOut = () => setPreviewZoom(Math.max(0.25, state.previewZoom / 1.25));

  const totalPages = layout.pages || 1;
  const currentPage = Math.min(state.currentPage, totalPages - 1);

  useEffect(() => {
    if (state.currentPage >= totalPages) setCurrentPage(0);
  }, [totalPages]); // eslint-disable-line

  return (
    <div
      ref={containerRef}
      className="flex-1 bg-[#090A0B] relative overflow-hidden flex flex-col"
      data-testid="canvas-area"
    >
      {/* Top status bar */}
      <div className="h-10 bg-[#121417] border-b border-[#31373E] flex items-center px-4 justify-between shrink-0">
        <div className="flex items-center gap-3 text-[10px] font-mono text-[#9CA3AF]">
          <span data-testid="canvas-status-cols">
            <span className="text-[#6B7280]">Cols×Filas: </span>
            <span className="text-[#F3F4F6]">{layout.cols} × {layout.rows}</span>
          </span>
          <span className="w-px h-3 bg-[#31373E]" />
          <span data-testid="canvas-status-perpage">
            <span className="text-[#6B7280]">Por hoja: </span>
            <span className="text-[#F3F4F6]">{layout.perPage}</span>
          </span>
          <span className="w-px h-3 bg-[#31373E]" />
          <span data-testid="canvas-status-pages">
            <span className="text-[#6B7280]">Hojas: </span>
            <span className="text-[#00E5FF] font-semibold">{layout.pages}</span>
          </span>
          <span className="w-px h-3 bg-[#31373E]" />
          <span>
            <span className="text-[#6B7280]">Copias: </span>
            <span className="text-[#F3F4F6]">{state.copies}</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            data-testid="page-prev-btn"
            className="h-7 w-7 flex items-center justify-center rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={14} />
          </button>
          <select
            value={currentPage}
            onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
            data-testid="page-select"
            className="h-7 bg-[#121417] border border-[#31373E] text-xs px-2 rounded-sm font-mono text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF] cursor-pointer"
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i} value={i}>{`Pág ${i + 1} / ${totalPages}`}</option>
            ))}
          </select>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage >= totalPages - 1}
            data-testid="page-next-btn"
            className="h-7 w-7 flex items-center justify-center rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight size={14} />
          </button>
          <div className="w-px h-5 bg-[#31373E] mx-1" />
          <button
            onClick={zoomOut}
            data-testid="zoom-out-btn"
            className="h-7 w-7 flex items-center justify-center rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]"
          >
            <ZoomOut size={14} />
          </button>
          <span className="text-[10px] font-mono text-[#9CA3AF] min-w-[40px] text-center">
            {Math.round(state.previewZoom * 100)}%
          </span>
          <button
            onClick={zoomIn}
            data-testid="zoom-in-btn"
            className="h-7 w-7 flex items-center justify-center rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]"
          >
            <ZoomIn size={14} />
          </button>
          <button
            onClick={fitToScreen}
            data-testid="fit-screen-btn"
            className="h-7 w-7 flex items-center justify-center rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]"
            title="Ajustar a pantalla"
          >
            <Maximize size={14} />
          </button>
        </div>
      </div>

      {/* Scrollable preview */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto p-8 flex items-start justify-center"
        data-testid="canvas-scroll"
      >
        {totalPages === 0 ? (
          <div className="text-center text-[#9CA3AF] mt-20" data-testid="canvas-empty">
            <p className="text-sm">La pieza no cabe en la hoja con la configuración actual.</p>
            <p className="text-xs text-[#6B7280] mt-2">
              Reduce el tamaño de la pieza o aumenta el área útil de la hoja.
            </p>
          </div>
        ) : (
          <PageView pageIndex={currentPage} pxPerMm={pxPerMm} />
        )}
      </div>
    </div>
  );
}
